import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { Button, Input, Label } from '@/components/ui'
import { ApiError, post } from '@/lib/api'
import { AuthCard, FormError } from './AuthCard'

export function ForgotPage() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  async function submit(e: FormEvent) {
    e.preventDefault()
    // Always "sent": the answer must not reveal whether the address has an account.
    await post('/auth/forgot', { email }).catch(() => undefined)
    setSent(true)
  }
  return (
    <AuthCard title="Reset password" footer={<Link to="/login">Back to sign in</Link>}>
      {sent ? (
        <p className="text-sm text-fg-muted">If that address has an account, a reset link is on its way. It expires in an hour.</p>
      ) : (
        <form onSubmit={submit}>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <Button type="submit" variant="primary" size="md" className="mt-4 w-full">
            Send reset link
          </Button>
        </form>
      )}
    </AuthCard>
  )
}

function PasswordForm({ submitLabel, onSubmit, withName }: { submitLabel: string; onSubmit: (v: { password: string; name?: string }) => Promise<void>; withName?: boolean }) {
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  async function submit(e: FormEvent) {
    e.preventDefault()
    if (password.length < 12) return setError('Use at least 12 characters.')
    if (password !== confirm) return setError("The two passwords don't match.")
    setBusy(true)
    setError(null)
    try {
      await onSubmit({ password, name: withName ? name : undefined })
    } catch (err) {
      setError(err instanceof ApiError ? (err.what ?? err.message) : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit}>
      <FormError>{error}</FormError>
      {withName && (
        <>
          <Label htmlFor="name">Your name</Label>
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mb-3" autoFocus />
        </>
      )}
      <Label htmlFor="pw">New password</Label>
      <Input id="pw" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      <Label htmlFor="pw2" className="mt-3">
        Confirm
      </Label>
      <Input id="pw2" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      <p className="mt-2 text-2xs text-fg-faint">At least 12 characters; common passwords are refused.</p>
      <Button type="submit" variant="primary" size="md" className="mt-4 w-full" disabled={busy}>
        {submitLabel}
      </Button>
    </form>
  )
}

/** The link's token: from the path (`/invite/<t>`) or, for older emails, `?token=`. */
function useToken(): string {
  const p = useParams({ strict: false }) as { token?: string }
  const q = useSearch({ strict: false }) as { token?: string }
  return p.token ?? q.token ?? ''
}

export function ResetPage() {
  const token = useToken()
  const nav = useNavigate()
  return (
    <AuthCard title="Choose a new password">
      <PasswordForm
        submitLabel="Set password"
        onSubmit={async ({ password }) => {
          await post('/auth/reset', { token, password })
          await nav({ to: '/login' })
        }}
      />
    </AuthCard>
  )
}

export function InvitePage() {
  const token = useToken()
  const nav = useNavigate()
  return (
    <AuthCard title="Join One Deployment" footer="Next you'll set up two-factor — it's required for everyone.">
      <PasswordForm
        withName
        submitLabel="Create account"
        onSubmit={async ({ password, name }) => {
          await post('/auth/invite/accept', { token, password, name })
          // A new person goes straight to enrolment (01 §3.2).
          await nav({ to: '/enrol' })
        }}
      />
    </AuthCard>
  )
}
